import React from "react";
import { Track } from "livekit-client";
import { useEffect, useMemo, useRef } from "react";
import { logger } from "library/core/utility";
import { logToGraylog } from "core/stores";

const DATA_STREAM_ID_ATTR = "data-stream-id";
const DATA_TRACK_SID_ATTR = "data-track-sid";

interface LiveKitTrackProps {
  tracks: Track[];
  volume?: number;
}

const handleTrackAttachment = (
  track: Track | null,
  elementRef: React.RefObject<HTMLMediaElement>,
  attachedTrackRef: React.MutableRefObject<Track | null>,
  trackKind: "video" | "audio",
  volume?: number
) => {
  const el = elementRef.current;
  if (!el) return;

  const previouslyAttachedTrack = attachedTrackRef.current;
  const isMuted = typeof volume === "number" && volume <= 0;

  if (track) {
    if (previouslyAttachedTrack !== track) {
      if (previouslyAttachedTrack) {
        logger.log(`[Livekit] Detaching old ${trackKind} track`, { sid: previouslyAttachedTrack.sid });
        logToGraylog("[Livekit]", `Detaching old ${trackKind} track`, { sid: previouslyAttachedTrack.sid });
        previouslyAttachedTrack.detach(el);
        el.removeAttribute(DATA_STREAM_ID_ATTR);
        el.removeAttribute(DATA_TRACK_SID_ATTR);
      }
      logger.log(`[Livekit] Attaching new ${trackKind} track`, { sid: track.sid, volume, isMuted });
      logToGraylog("[Livekit]", `Attaching new ${trackKind} track`, { sid: track.sid, volume, isMuted });
      track.attach(el);
      // Set volume/muted AFTER attach for iOS Safari compatibility
      if (trackKind === "audio" && el instanceof HTMLAudioElement) {
        el.muted = isMuted;
        if (typeof volume === "number") {
          el.volume = volume;
        }
      }
      el.setAttribute(DATA_STREAM_ID_ATTR, track.mediaStream?.id || "");
      el.setAttribute(DATA_TRACK_SID_ATTR, track.sid || "");
      attachedTrackRef.current = track;
    } else {
      // Same track, just update volume/muted
      if (trackKind === "audio" && el instanceof HTMLAudioElement) {
        const needsUpdate = el.muted !== isMuted || (typeof volume === "number" && el.volume !== volume);
        if (needsUpdate) {
          logger.log(`[Livekit] Updating volume for ${trackKind} track`, { sid: track.sid, volume, isMuted });
          logToGraylog("[Livekit]", `Updating volume for ${trackKind} track`, { sid: track.sid, volume, isMuted });
          el.muted = isMuted;
          if (typeof volume === "number") {
            el.volume = volume;
          }
        }
      }
    }
  } else {
    if (previouslyAttachedTrack) {
      logger.log(`[Livekit] Detaching ${trackKind} track (target is null)`, { sid: previouslyAttachedTrack.sid });
      logToGraylog("[Livekit]", `Detaching ${trackKind} track (target is null)`, { sid: previouslyAttachedTrack.sid });
      previouslyAttachedTrack.detach(el);
      el.removeAttribute(DATA_STREAM_ID_ATTR);
      el.removeAttribute(DATA_TRACK_SID_ATTR);
      attachedTrackRef.current = null;
    }
  }

  return () => {
    if (track && el && attachedTrackRef.current === track) {
      logger.log(`[Livekit] Detaching ${trackKind} track (cleanup for this instance)`, { sid: track.sid });
      logToGraylog("[Livekit]", `Detaching ${trackKind} track (cleanup for this instance)`, { sid: track.sid });
      track.detach(el);
      el.removeAttribute(DATA_STREAM_ID_ATTR);
      el.removeAttribute(DATA_TRACK_SID_ATTR);
      attachedTrackRef.current = null;
    }
  };
};

export const LiveKitTrack: React.FC<LiveKitTrackProps> = ({
  tracks,
  volume = 0.8,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const attachedVideoTrackRef = useRef<Track | null>(null);
  const attachedAudioTrackRef = useRef<Track | null>(null);

  const videoTrack = useMemo(() => {
    const track = tracks.find(t => t.kind === Track.Kind.Video) || null;
    return track;
  }, [tracks]);

  const audioTrack = useMemo(() => {
    const track = tracks.find(t => t.kind === Track.Kind.Audio) || null;
    return track;
  }, [tracks]);

  useEffect(() => {
    return handleTrackAttachment(videoTrack, videoRef, attachedVideoTrackRef, "video");
  }, [videoTrack]);

  useEffect(() => {
    return handleTrackAttachment(audioTrack, audioRef, attachedAudioTrackRef, "audio", volume);
  }, [audioTrack, volume]);

  return (
    <>
      <video
        ref={videoRef}
        className='w-full h-full'
        autoPlay
        playsInline
        controls={false}
        muted
      />
      <audio ref={audioRef} autoPlay muted={volume <= 0} />
    </>
  );
};
